import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { OccurrenceCategory, OccurrenceType, OccurrenceSeverity } from '../types';
import { occurrenceService } from '../occurrenceService';
import { getActiveSessionOrganizationCode } from '../../../../shared/api';
import { useEscapeKey } from '../../../../shared/useGlobalModalEscape';
import { useDialog } from '../../../../shared/DialogContext';

interface CreateOccurrenceModalProps {
  onClose: () => void;
  onCreated: () => void;
}

export const CreateOccurrenceModal: React.FC<CreateOccurrenceModalProps> = ({
  onClose,
  onCreated,
}) => {
  useEscapeKey(onClose);
  const { showError, showWarning } = useDialog();

  const [categories, setCategories] = useState<OccurrenceCategory[]>([]);
  const [types, setTypes] = useState<OccurrenceType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [eventDate, setEventDate] = useState<string>(new Date().toISOString().slice(0, 16));
  const [severity, setSeverity] = useState<OccurrenceSeverity>('NORMAL');
  const [isMandatory, setIsMandatory] = useState<boolean>(false);
  const [flightNumber, setFlightNumber] = useState<string>('');
  const [aircraftReg, setAircraftReg] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    occurrenceService.getCategories().then((cats) => {
      setCategories(cats);
      if (cats.length > 0) {
        setSelectedCategory(cats[0].id);
      }
    });
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      occurrenceService.getOccurrenceTypes(selectedCategory).then((t) => {
        setTypes(t);
        if (t.length > 0) {
          setSelectedType(t[0].id);
          setIsMandatory(t[0].isMandatoryDefault);
        }
      });
    }
  }, [selectedCategory]);

  const handleTypeChange = (typeId: string) => {
    setSelectedType(typeId);
    const found = types.find((t) => t.id === typeId);
    if (found) {
      setIsMandatory(found.isMandatoryDefault);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !selectedCategory || !selectedType) {
      showWarning('Please fill all required fields.', 'Missing Required Information');
      return;
    }

    try {
      setLoading(true);
      await occurrenceService.createOccurrence({
        organizationId: getActiveSessionOrganizationCode() || undefined,
        title: title.trim(),
        description: description.trim(),
        eventDate: new Date(eventDate).toISOString(),
        severity,
        categoryId: selectedCategory,
        typeId: selectedType,
        isMandatory,
        flightNumber: flightNumber.trim() || undefined,
        aircraftReg: aircraftReg.trim() || undefined,
        location: location.trim() || undefined,
        createdBy: 'SAFETY_OPERATOR',
      });
      onCreated();
    } catch (err: any) {
      showError(err, 'Failed to Create Occurrence');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>New Safety Occurrence Record</h3>
            <p>Mandatory and voluntary aviation occurrence registration</p>
          </div>
          <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-field">
              <label>Occurrence Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Flap asymmetry alert on approach"
                required
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Taxonomy Category *</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Event Type (ICAO) *</label>
                <select
                  value={selectedType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                >
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} {t.icaoCode ? `(${t.icaoCode})` : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Event Date (Local/UTC) *</label>
                <input
                  type="datetime-local"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Severity Level *</label>
                <select
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as OccurrenceSeverity)}
                >
                  <option value="CRITICAL">CRITICAL (High risk / damage)</option>
                  <option value="HIGH">HIGH (Serious operational impact)</option>
                  <option value="NORMAL">NORMAL (Standard incident)</option>
                  <option value="LOW">LOW (Minor occurrence)</option>
                </select>
              </div>
            </div>

            <div className="form-grid-3">
              <div className="form-field">
                <label>Flight Number</label>
                <input
                  type="text"
                  value={flightNumber}
                  onChange={(e) => setFlightNumber(e.target.value)}
                  placeholder="e.g. TK1984"
                />
              </div>
              <div className="form-field">
                <label>Aircraft Reg</label>
                <input
                  type="text"
                  value={aircraftReg}
                  onChange={(e) => setAircraftReg(e.target.value)}
                  placeholder="e.g. TC-JSN"
                />
              </div>
              <div className="form-field">
                <label>Location / Airport</label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. LTFM / IST"
                />
              </div>
            </div>

            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isMandatory}
                  onChange={(e) => setIsMandatory(e.target.checked)}
                />
                <span style={{ fontWeight: 600, color: isMandatory ? '#b91c1c' : 'inherit' }}>
                  Mandatory Occurrence Reporting (MOR) — Triggers strict 72-Hour EASA/ICAO deadline clock
                </span>
              </label>
            </div>

            <div className="form-field">
              <label>Safety Narrative & Description *</label>
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide detailed narrative of the occurrence..."
                required
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Creating...' : 'Create Occurrence'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
